import Cookies from "js-cookie";

export const AUTH_CHANGED_EVENT =
    "mexfba-auth-changed";

const AUTH_STATE_KEY =
    "mexfba-auth-state";

const CSRF_TOKEN_KEY =
    "mexfba-csrf-token";

/*
    Compatibilidad temporal con pantallas React todavía no migradas.
    Debe coincidir con FANTASY_LEGACY_READ_AUTH_EXPIRES_AT del backend.
*/
const LEGACY_READ_AUTH_EXPIRES_AT =
    new Date("2026-10-21T23:59:59-06:00");

const LEGACY_MUTATION_AUTH_EXPIRES_AT =
    new Date("2026-10-17T23:59:59-06:00");

    function getLegacyAuthenticatedUser():
    MexfbaAuthenticatedUser | null {
    if (new Date() > LEGACY_READ_AUTH_EXPIRES_AT) {
        return null;
    }

    const id = Cookies.get("access_token") || "";
    const username =
        Cookies.get("access_username") || "";

    if (!id || !username) {
        return null;
    }

    return {
        id,
        username,
        name:
            Cookies.get("access_user") ||
            username,
        role: "user"
    };
}

export interface MexfbaAuthenticatedUser {
    id: string;
    username: string;
    name: string;
    role: string;
}

interface StoredAuthState {
    user: MexfbaAuthenticatedUser;
}

interface AuthStateResponse {
    success: boolean;
    authenticated: boolean;
    csrfToken?: string;
    user?: MexfbaAuthenticatedUser;
    message?: string;
}

interface LoginResponse {
    success: boolean;
    csrfToken?: string;
    user?: MexfbaAuthenticatedUser;
    message?: string;
}

function getApiBaseUrl(): string {
    const configuredUrl = String(
        process.env.REACT_APP_FANTASY_API_BASE_URL || ""
    ).trim();

    if (configuredUrl !== "") {
        return configuredUrl.replace(/\/+$/, "");
    }

    return (
        process.env.PUBLIC_URL || ""
    ) + "/create_league";
}

function notifyAuthenticationChanged(): void {
    window.dispatchEvent(
        new Event(AUTH_CHANGED_EVENT)
    );
}

function readStoredAuthState(): StoredAuthState | null {
    try {
        const rawValue = sessionStorage.getItem(
            AUTH_STATE_KEY
        );

        if (!rawValue) {
            return null;
        }

        const parsedValue = JSON.parse(rawValue);

        if (
            !parsedValue ||
            typeof parsedValue !== "object" ||
            !parsedValue.user ||
            typeof parsedValue.user !== "object"
        ) {
            return null;
        }

        const user = parsedValue.user as MexfbaAuthenticatedUser;

        if (!user.id || !user.username) {
            return null;
        }

        return {
            user
        };
    } catch {
        return null;
    }
}

function storeAuthenticatedState(
    user: MexfbaAuthenticatedUser,
    csrfToken: string
): void {
    sessionStorage.setItem(
        AUTH_STATE_KEY,
        JSON.stringify({
            user
        })
    );

    sessionStorage.setItem(
        CSRF_TOKEN_KEY,
        csrfToken
    );

    /*
        Compatibilidad temporal.

        Las pantallas antiguas todavía leen cookies mediante
        js-cookie. Estas cookies se eliminarán definitivamente
        al completar la migración de React.
    */
    if (new Date() <= LEGACY_READ_AUTH_EXPIRES_AT) {
        const cookieOptions = {
            expires: LEGACY_READ_AUTH_EXPIRES_AT,
            sameSite: "lax" as const,
            secure:
                window.location.protocol === "https:",
            path: "/"
        };

        Cookies.set(
            "access_token",
            user.id,
            cookieOptions
        );

        Cookies.set(
            "refresh_token",
            user.id,
            cookieOptions
        );

        Cookies.set(
            "access_username",
            user.username,
            cookieOptions
        );

        Cookies.set(
            "access_user",
            user.name,
            cookieOptions
        );
    }

    notifyAuthenticationChanged();
}

function removeLegacyCookies(): void {
    const cookieOptions = {
        path: "/"
    };

    Cookies.remove("access_token", cookieOptions);
    Cookies.remove("refresh_token", cookieOptions);
    Cookies.remove("access_username", cookieOptions);
    Cookies.remove("access_user", cookieOptions);
}

export function getAuthenticatedUser():
    MexfbaAuthenticatedUser | null {
    return readStoredAuthState()?.user || null;
}

export function getAuthenticatedUsername():
    string | null {
    return (
        getAuthenticatedUser()?.username ||
        Cookies.get("access_username") ||
        null
    );
}

export function getCsrfToken(): string {
    return sessionStorage.getItem(
        CSRF_TOKEN_KEY
    ) || "";
}

export function hasSecureSessionState(): boolean {
    return (
        getAuthenticatedUser() !== null &&
        getCsrfToken() !== ""
    );
}

export function getAuthenticatedUserId(): string | null {
    return (
        getAuthenticatedUser()?.id ||
        Cookies.get("access_token") ||
        null
    );
}

/*
    Temporalmente retorna el ID interno porque pantallas viejas
    siguen usando el valor como identificador visual.

    Nunca debe usarse como credencial nueva.
*/
export function getAccessToken(): string | null {
    return (
        getAuthenticatedUser()?.id ||
        Cookies.get("access_token") ||
        null
    );
}

export function getRefreshToken(): string | null {
    return Cookies.get("refresh_token") || null;
}

export function isAuthenticated(): boolean {
    return getAuthenticatedUser() !== null ||
        Boolean(
            Cookies.get("access_token") &&
            Cookies.get("access_username")
        );
}

/*
    Compatibilidad con código React antiguo que todavía llama:
    setAuthentication(userId, userName, username, expiresAt)
*/
export function setAuthentication(
    userId: string,
    userName: string,
    username: string,
    _expiresAt?: Date
): void {
    storeAuthenticatedState(
        {
            id: userId,
            username,
            name: userName,
            role: "user"
        },
        getCsrfToken()
    );
}

export function clearAuthentication(): void {
    sessionStorage.removeItem(AUTH_STATE_KEY);
    sessionStorage.removeItem(CSRF_TOKEN_KEY);

    removeLegacyCookies();

    notifyAuthenticationChanged();
}

export async function refreshAuthentication(): Promise<
    MexfbaAuthenticatedUser | null
> {
    const response = await fetch(
        getApiBaseUrl() + "/control_AuthState.php",
        {
            method: "GET",
            credentials: "include",
            cache: "no-store",
            headers: {
                Accept: "application/json"
            }
        }
    );

    const result =
        (await response.json()) as AuthStateResponse;

    if (
    !response.ok ||
    !result.success ||
    !result.authenticated ||
    !result.user ||
    !result.csrfToken
) {
    /*
        Durante transición no se eliminan cookies antiguas
        si el usuario todavía tiene una sesión legacy válida.
    */
    const legacyUser =
        getLegacyAuthenticatedUser();

    if (legacyUser !== null) {
        return legacyUser;
    }

    clearAuthentication();

    return null;
}
    storeAuthenticatedState(
        result.user,
        result.csrfToken
    );

    return result.user;
}

export async function loginFantasy(
    username: string,
    password: string
): Promise<{
    user: MexfbaAuthenticatedUser;
    csrfToken: string;
}> {
    const response = await fetch(
    getApiBaseUrl() + "/control_Login.php",
    {
        method: "POST",
        credentials: "include",
        cache: "no-store",
        headers: {
            Accept: "application/json",
            "Content-Type": "application/json"
        },
        body: JSON.stringify({
            username,
            password
        })
    }
);

const rawResponse = await response.text();

let result: LoginResponse;

try {
    result = JSON.parse(rawResponse) as LoginResponse;
} catch {
    console.error(
        "Login devolvió una respuesta no JSON:",
        {
            status: response.status,
            statusText: response.statusText,
            body: rawResponse
        }
    );

    throw new Error(
        rawResponse.trim() !== ""
            ? "El servidor devolvió una respuesta inválida."
            : "El servidor no devolvió respuesta. Revisa el log PHP."
    );
}

if (
    !response.ok ||
    !result.success ||
    !result.user ||
    !result.csrfToken
) {
    throw new Error(
        result.message ||
            "No fue posible iniciar sesión."
    );
}

    storeAuthenticatedState(
        result.user,
        result.csrfToken
    );

    return {
        user: result.user,
        csrfToken: result.csrfToken
    };
}

export async function logoutFantasy(): Promise<void> {
    const csrfToken = getCsrfToken();

    try {
        await fetch(
            getApiBaseUrl() + "/control_Logout.php",
            {
                method: "POST",
                credentials: "include",
                cache: "no-store",
                headers: {
                    Accept: "application/json",
                    "Content-Type": "application/json",
                    "X-CSRF-Token": csrfToken
                },
                body: JSON.stringify({})
            }
        );
    } finally {
        clearAuthentication();
    }
}